import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  assignCourseIntake,
  createCourseIntake,
  deleteCourseIntake,
  getCourseIntakes,
  getCourses,
  getPrograms,
  updateCourseIntake,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { GraduationCap, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

const STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_TONE = {
  planning: 'bg-slate-100 text-slate-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-amber-100 text-amber-800',
};

const EMPTY = {
  program_id: '', intake_label: '', reference_code: '',
  display_name_override: '', start_date: '', end_date: '', status: 'planning',
};

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-[#062B1A]/40">{hint}</p>}
    </div>
  );
}

/**
 * Course intakes — the registrar's view of who is being taught what, when.
 *
 * An intake is one run of a program ("FSL August 2026") holding that run's
 * courses. Its id is opaque and never shown; staff work with the display name
 * and a short reference code they can quote in a support conversation.
 */
export default function AdminCourseIntakes() {
  const [intakes, setIntakes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [movingId, setMovingId] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [intakeRes, programRes, courseRes] = await Promise.all([
        getCourseIntakes(),
        getPrograms(),
        getCourses(),
      ]);
      setIntakes(intakeRes.data || []);
      setPrograms(programRes.data?.items || programRes.data || []);
      setCourses(courseRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load course intakes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const coursesByIntake = useMemo(() => {
    const map = { '': [] };
    intakes.forEach((intake) => { map[intake.id] = []; });
    courses.forEach((course) => {
      const key = course.intake_id || '';
      (map[key] = map[key] || []).push(course);
    });
    Object.values(map).forEach((list) => list.sort(
      (a, b) => String(a.course_code || a.title).localeCompare(String(b.course_code || b.title)),
    ));
    return map;
  }, [courses, intakes]);

  const openCreate = () => { setDialog({ mode: 'create' }); setForm(EMPTY); };
  const openEdit = (intake) => {
    setDialog({ mode: 'edit', intake });
    setForm({
      program_id: intake.program_id || '',
      intake_label: intake.intake_label || '',
      reference_code: intake.reference_code || '',
      display_name_override: intake.display_name_override || '',
      start_date: intake.start_date || '',
      end_date: intake.end_date || '',
      status: intake.status || 'planning',
    });
  };

  const handleSave = async () => {
    if (dialog.mode === 'create' && !form.program_id) {
      toast.error('Pick the program this intake runs');
      return;
    }
    if (!form.intake_label.trim()) {
      toast.error('Give the intake a label, e.g. August 2026');
      return;
    }
    try {
      setSaving(true);
      if (dialog.mode === 'create') {
        await createCourseIntake(form);
        toast.success('Intake created');
      } else {
        const { program_id: _ignored, ...rest } = form;
        await updateCourseIntake(dialog.intake.id, rest);
        toast.success('Intake updated');
      }
      setDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save this intake');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await deleteCourseIntake(pendingDelete.id);
      toast.success('Intake deleted');
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete this intake');
    } finally {
      setSaving(false);
    }
  };

  const moveCourse = async (course, intakeId) => {
    try {
      setMovingId(course.id);
      await assignCourseIntake(course.id, intakeId === '__none__' ? '' : intakeId);
      toast.success(`${course.course_code || course.title} moved`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not move that course');
    } finally {
      setMovingId('');
    }
  };

  const intakeOptions = [
    { value: '__none__', label: 'Unassigned' },
    ...intakes.map((intake) => ({ value: intake.id, label: intake.display_name })),
  ];

  const unassigned = coursesByIntake[''] || [];

  if (loading) {
    return <div className="flex min-h-64 items-center justify-center"><Loader2 className="size-7 animate-spin text-[#D4AF37]" /></div>;
  }

  const renderCourseRow = (course) => (
    <div key={course.id} className="grid gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3 sm:grid-cols-[1fr_240px] sm:items-center">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-[#062B1A]">
          {course.course_code ? `${course.course_code} — ` : ''}{course.title}
        </p>
        <p className="text-xs text-[#062B1A]/45">
          {course.term ? `Starts ${course.term}` : 'No start term'} · {course.status || 'draft'}
        </p>
      </div>
      <Select
        value={course.intake_id || '__none__'}
        disabled={movingId === course.id}
        onValueChange={(value) => moveCourse(course, value)}
      >
        <SelectTrigger aria-label={`Intake for ${course.course_code || course.title}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {intakeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Course Intakes
          </h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            One intake is one run of a program. Its courses appear together in everyone's sidebar.
          </p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={openCreate}>
          <Plus className="mr-2 size-4" />New intake
        </Button>
      </div>

      {intakes.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-[#062B1A]/50">
          No intakes yet. Create one, then file its courses below.
        </CardContent></Card>
      )}

      {intakes.map((intake) => (
        <Card key={intake.id} className="border-[rgba(6,43,26,0.08)]">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]">
                  <GraduationCap className="size-4 text-[#D4AF37]" />
                  {intake.display_name}
                  <Badge className={`border-0 text-[10px] ${STATUS_TONE[intake.status] || STATUS_TONE.planning}`}>
                    {intake.status}
                  </Badge>
                </CardTitle>
                <p className="mt-1 text-xs text-[#062B1A]/45">
                  {intake.program_name}
                  {intake.reference_code ? ` · ref ${intake.reference_code}` : ''}
                  {` · ${intake.course_count || 0} course${(intake.course_count || 0) === 1 ? '' : 's'}`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(intake)}>
                  <Pencil className="mr-1.5 size-3.5" />Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={() => setPendingDelete(intake)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(coursesByIntake[intake.id] || []).length === 0
              ? <p className="py-3 text-sm text-[#062B1A]/40">No courses filed here yet.</p>
              : (coursesByIntake[intake.id] || []).map(renderCourseRow)}
          </CardContent>
        </Card>
      ))}

      {unassigned.length > 0 && (
        <Card className="border-dashed border-amber-300 bg-amber-50/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A]">Unassigned courses</CardTitle>
            <p className="text-sm text-[#062B1A]/55">
              These belong to no intake, so they do not appear in the sidebar tree. They are still
              reachable through My Courses. File them to group them for their cohort.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">{unassigned.map(renderCourseRow)}</CardContent>
        </Card>
      )}

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {dialog?.mode === 'create' ? 'New intake' : 'Edit intake'}
            </DialogTitle>
            <DialogDescription>
              The label is what everyone sees in the sidebar. The reference code is for staff.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {dialog?.mode === 'create' && (
              <Field label="Program *">
                <Select value={form.program_id} onValueChange={(v) => setForm((p) => ({ ...p, program_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select a program" /></SelectTrigger>
                  <SelectContent>
                    {programs.map((program) => (
                      <SelectItem key={program.id} value={program.id}>
                        {program.code ? `${program.code} — ` : ''}{program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Intake label *" hint='e.g. "August 2026". Shown as "FSL August 2026".'>
              <Input
                value={form.intake_label}
                onChange={(e) => setForm((p) => ({ ...p, intake_label: e.target.value }))}
                placeholder="August 2026"
              />
            </Field>
            <Field label="Display name override" hint="Leave blank to use the program short form plus the label.">
              <Input
                value={form.display_name_override}
                onChange={(e) => setForm((p) => ({ ...p, display_name_override: e.target.value }))}
                placeholder="FSL Evening Cohort"
              />
            </Field>
            <Field label="Reference code" hint="Lowercase letters, numbers and single hyphens. Generated if left blank.">
              <Input
                value={form.reference_code}
                onChange={(e) => setForm((p) => ({ ...p, reference_code: e.target.value }))}
                placeholder="fsl-august-2026"
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Starts">
                <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} />
              </Field>
              <Field label="Ends">
                <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} />
              </Field>
            </div>
            <Field label="Status" hint="Only planning, active and completed intakes show in an instructor or student sidebar.">
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" disabled={saving} onClick={handleSave}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {dialog?.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              Delete {pendingDelete?.display_name}?
            </DialogTitle>
            <DialogDescription>
              Courses filed under an intake must be moved first — deleting one never detaches them
              silently. The courses themselves are never deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button className="bg-red-700 text-white hover:bg-red-800" disabled={saving} onClick={handleDelete}>
              Delete intake
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
